import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Gem, Scissors, Shield, Zap, Eye, Target, Check, Sparkles, ArrowRight } from 'lucide-react';

const getTechniqueData = (language) => ({
  badge: language === 'de' ? 'Modernste Technologie' : 'Latest Technology',
  sectionTitle: language === 'de' ? 'Unsere ' : 'Our ',
  sectionTitleAccent: language === 'de' ? 'Technik' : 'Technique',
  sectionSubtitle: language === 'de'
    ? 'Die fortschrittlichste Methode der Haartransplantation — individuell auf Ihre Bedürfnisse abgestimmt.'
    : 'The most advanced method of hair transplantation — individually tailored to your needs.',
  name: 'Saphir DHI Hybrid',
  subtitle: language === 'de' ? 'Das Beste aus beiden Welten' : 'The best of both worlds',
  idealFor: language === 'de' ? 'Ohne Rasur & maximale Dichte' : 'No shaving & maximum density',
  image: '/images/optimized/saphir-dhi.webp',
  description: language === 'de'
    ? 'Unsere Saphir DHI Hybridmethode vereint die Präzision der Saphir-Klingen mit der direkten Implantation des Choi-Pens. Feinste Kanalöffnungen für schnellere Heilung, direkte Implantation für maximale Kontrolle über Winkel und Dichte — komplett ohne Rasur des Deckhaars.'
    : 'Our Sapphire DHI Hybrid method combines the precision of sapphire blades with the direct implantation of the Choi Pen. Finest channel openings for faster healing, direct implantation for maximum control over angle and density — completely without shaving existing hair.',
  stats: [
    { value: '100%', label: language === 'de' ? 'Ohne Rasur Oberkopf' : 'No scalp shaving' },
    { value: '95%+', label: language === 'de' ? 'Anwachsrate' : 'Growth rate', hasAsterisk: true },
    { value: '7–10', label: language === 'de' ? 'Tage Heilung' : 'Days healing' },
  ],
  features: [
    { icon: Gem, text: language === 'de' ? 'Saphir-Klingen für feinere Inzisionen' : 'Sapphire blades for finer incisions' },
    { icon: Scissors, text: language === 'de' ? 'Ohne Rasur des Deckhaars' : 'No shaving of existing hair' },
    { icon: Target, text: language === 'de' ? 'Maximale Haardichte (80–100/cm²)' : 'Maximum hair density (80–100/cm²)' },
    { icon: Zap, text: language === 'de' ? 'Schnellere Heilung als Standard' : 'Faster healing than standard' },
  ],
  benefits: language === 'de'
    ? ['Natürlichste Wuchsrichtung durch Choi-Pen', 'Dichtere Graft-Platzierung durch Saphir', 'Kürzeste Heilungszeit aller Methoden', 'Ideal für Verdichtung & Haarlinie']
    : ['Most natural growth direction via Choi Pen', 'Denser graft placement with sapphire', 'Shortest healing time of all methods', 'Ideal for densification & hairline'],
  ctaQuestion: language === 'de'
    ? 'Finden Sie heraus, ob die Saphir DHI Hybridmethode für Sie geeignet ist.'
    : 'Find out if the Sapphire DHI Hybrid method is right for you.',
  ctaText: language === 'de' ? 'Kostenlose Beratung erhalten' : 'Get Free Consultation',
  disclaimer: language === 'de'
    ? '* Die Anwachsrate variiert je nach Behandlungsregion und individueller Veranlagung.'
    : '* Growth rate varies depending on treatment region and individual predisposition.',
});

const DhiHybridHighlight = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  if (!features?.showDhiHybridOnly) return null;

  const t = getTechniqueData(language);

  return (
    <section
      id="techniken"
      className={`relative py-16 sm:py-24 overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0a0a0a]' : 'bg-white'
      }`}
      data-testid="dhi-hybrid-highlight-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 sm:mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
          }`}>
            <Gem className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
              {t.badge}
            </span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.sectionTitle}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {t.sectionTitleAccent}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-base sm:text-lg px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Single Technique Card */}
        <div className="max-w-4xl mx-auto">
          <div className={`group relative rounded-2xl sm:rounded-3xl border overflow-hidden transition-all duration-500 ${
            isDark
              ? 'bg-gradient-to-br from-white/[0.05] to-white/[0.02] border-white/10'
              : 'bg-white border-gray-200 shadow-lg'
          }`}>
            {/* Decorative Background */}
            <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] opacity-10 rounded-full blur-[100px]" />

            {/* Technique Image */}
            <div className="relative overflow-hidden">
              <img
                src={t.image}
                alt={`${t.name} - DHI Haartransplantation ohne Rasur Dion Hair Clinic`}
                loading="lazy"
                className="w-full h-56 sm:h-64 lg:h-72 object-cover object-center"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0a0a0a]/70' : 'from-white/50'} to-transparent`} />
              <div className="absolute bottom-4 left-4">
                <span className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium backdrop-blur-sm ${
                  isDark ? 'bg-black/40 text-[#7BA7C2]' : 'bg-white/80 text-[#5A8BA6]'
                }`}>
                  {t.idealFor}
                </span>
              </div>
            </div>

            {/* Card Content */}
            <div className="relative p-5 sm:p-8 lg:p-10">
              {/* Header */}
              <div className="mb-6 sm:mb-8">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                  <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t.name}
                  </h3>
                </div>
                <p className={`text-sm sm:text-base ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'} font-medium`}>
                  {t.subtitle}
                </p>
              </div>

              {/* Description */}
              <p className={`text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {t.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
                {t.stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`text-center p-3 sm:p-4 rounded-xl h-[90px] sm:h-[100px] flex flex-col justify-center ${
                      isDark
                        ? 'bg-white/[0.03] border border-white/5'
                        : 'bg-gray-50 border border-gray-100'
                    }`}
                    data-testid={`hybrid-stat-${index}`}
                  >
                    <div className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stat.hasAsterisk ? stat.value.replace('+', '') + '+' : stat.value}
                      {stat.hasAsterisk && <span className={`text-[10px] align-super text-gray-400`}>*</span>}
                    </div>
                    <div className={`text-[10px] sm:text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {t.features.map((feature, index) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl ${
                        isDark
                          ? 'bg-white/[0.02] border border-white/5'
                          : 'bg-gray-50 border border-gray-100'
                      }`}
                      data-testid={`hybrid-feature-${index}`}
                    >
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center flex-shrink-0">
                        <FeatureIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <span className={`text-sm sm:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {feature.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Benefits List */}
              <div className={`p-4 sm:p-6 rounded-xl ${
                isDark
                  ? 'bg-[#7BA7C2]/5 border border-[#7BA7C2]/20'
                  : 'bg-[#5A8BA6]/5 border border-[#5A8BA6]/20'
              }`}>
                <h4 className={`font-semibold mb-3 sm:mb-4 text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {language === 'de' ? 'Vorteile dieser Technik:' : 'Benefits of this technique:'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {t.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                      <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className={`mb-4 sm:mb-6 text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.ctaQuestion}
          </p>
          <button
            onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-base sm:text-lg font-medium shadow-lg shadow-[#7BA7C2]/25 transition-all duration-300 hover:scale-105"
            data-testid="hybrid-cta-btn"
          >
            <Sparkles className="w-5 h-5" />
            {t.ctaText}
          </button>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center">
          <p className={`text-xs max-w-2xl mx-auto ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {t.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DhiHybridHighlight;
